import React, { type FC } from "react";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import EcoItems from "../EcoItems";
import styles from "./EngineSelect.module.scss";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import type { MotorizationItemType } from "../../../../../utils/gradeUtils";
import EngineSpecs from "./EngineSpecs";
import { GradeSelectorLabel } from "../../../../../utils/labels";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import a11y from "../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import AvailabilityBadges from "../../../../../../shared-components/css-modules/lexus/organisms/AvailabilityBadges";

type EngineSelectProps = {
    motorization: MotorizationItemType;
    modelId: string;
    setSelectedMotorization?: (motorization: MotorizationItemType) => void;
    active?: boolean;
    summary?: boolean;
    showSpecs?: boolean;
    availableForOnlineSales?: boolean;
};

const EngineSelect: FC<{ props: EngineSelectProps }> = ({ props }) => {
    const {
        motorization,
        modelId,
        setSelectedMotorization,
        active = false,
        summary = false,
        showSpecs = false,
        availableForOnlineSales,
    } = props;

    return (
        // This should be a button accessibility wise. But this would result in a button inside a button, which creates hydration errors. However a button was added making the onclick of div a UX enhancement and inner part is still accessible
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
            onClick={setSelectedMotorization && (() => setSelectedMotorization(motorization))}
            className={clsx(styles.wrapper, summary && styles.summary, active && styles.active)}
        >
            <div className={styles["top-section"]}>
                <div className={styles.text}>
                    <div className={styles["eco-incentive"]}>
                        <EcoItems
                            modelId={modelId}
                            engines={[motorization.engine]}
                            limit={2}
                            disable={{ ecoBadge: true, efficiencyLabel: true }}
                            hasAdditionalIncentives={
                                !!motorization.engine.soldOut ||
                                !!motorization.engine.limitedStock ||
                                !!availableForOnlineSales
                            }
                        />
                        <AvailabilityBadges
                            isSoldOut={motorization.engine.soldOut}
                            isLimitedStock={motorization.engine.limitedStock}
                            isAvailableForOnlineSales={availableForOnlineSales}
                        />
                    </div>

                    <Text variant="apheleia-body">{motorization.engine.name}</Text>
                    <Text variant="apheleia-body">{`${motorization.wheeldrive.name}, ${motorization.transmission.name}`}</Text>
                </div>
                {!summary && active && (
                    <div className={styles.icon}>
                        <Icon sizing="xs" variant="check" color="grey-brushed-steel" aria-hidden="true" />
                    </div>
                )}
            </div>

            {showSpecs && (
                <EngineSpecs
                    ariaHidden
                    engine={motorization.engine}
                    transmission={motorization.transmission}
                    wheeldrive={motorization.wheeldrive}
                    className={styles.specs}
                />
            )}

            {!summary &&
                (active ? (
                    <div className={styles.selected}>
                        <span className={styles.selectedIcon}>
                            <Icon variant="check" aria-hidden="true" sizing="xs" />
                        </span>
                        <GradeSelectorLabel label="gradeSelectorEngineSelected" />
                    </div>
                ) : (
                    <Button
                        variant="apheleia-text"
                        onClick={setSelectedMotorization && (() => setSelectedMotorization(motorization))}
                        className={styles.selectEngine}
                    >
                        <GradeSelectorLabel label="gradeSelectorEngineChange" />{" "}
                        <span className={a11y["sr-only"]}>({motorization.engine.name})</span>
                    </Button>
                ))}
        </div>
    );
};

export default EngineSelect;
